import { EditIcon, InfoIcon } from 'assets/icons';
import cardStyles from 'common/settings-layout/settings-card.module.scss';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { VaultNavigationTabs, NotificationType } from 'common/enums';
import { HelperText } from 'common/helper-text';
import { LabeledText } from 'common/labeled-text';
import { Select } from 'common/select';
import { Toggle } from 'common/toggle';
import { frequencyOptions } from 'config/constants';
import { appActions, AppContext } from 'context';
import { classnames, isReadOnlySubOrRestrictedVault } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { useContext, useEffect, useState } from 'react';
import { goToPage, RouteName } from 'routes';
import styles from './reminders-section.module.scss';

type ReminderSectionProps = {
  vault: VaultType;
};

const nameAndEmail = (name: string, email: string) => `${name} (${email})`;

const searchUserName = (id: number, vault: VaultType) => {
  const user = vault.contributors.find(
    (contributor) => contributor.invitationId === id,
  );
  if (user) {
    return nameAndEmail(user.name, user.email);
  }
  if (vault.owner.id === id) {
    return nameAndEmail(vault.owner.name, vault.owner.email);
  }
  return '';
};

const createTheVaultOptions = (vault: VaultType) => {
  const options = vault.contributors.map(
    (contributor) =>
      ({
        id: contributor.invitationId,
        value: nameAndEmail(contributor.name, contributor.email),
      }) as SelectType,
  );
  options.unshift({
    id: 0,
    value: nameAndEmail(vault.owner.name, vault.owner.email),
  });

  return options;
};

const showFrequency = (frequency: string) => {
  const option = frequencyOptions.find(
    (opt: SelectType) => opt.id === frequency,
  );
  return option?.value;
};

const ReminderSection = ({ vault }: ReminderSectionProps) => {
  const { dispatch } = useContext(AppContext);
  const [remindersEnabled, setRemindersEnabled] = useState<boolean>(
    !!vault.reminder,
  );
  const [editReminders, setEditReminders] = useState<boolean>(false);
  const [personToRemind, setPersonToRemind] = useState<SelectType | undefined>(
    undefined,
  );
  const [frequency, setFrequency] = useState<SelectType>(frequencyOptions[0]);
  const disabled = isReadOnlySubOrRestrictedVault(vault);
  const vaultReminder = vault.reminder ? vault.reminder : null;
  const [reminder, setReminder] = useState<ReminderType | null>(vaultReminder);

  const handleSaveReminder = async () => {
    if (!personToRemind) {
      return;
    }

    const targetIsOwner = personToRemind.id === 0;
    const contributorTarget = vault.contributors.find(
      (contributor) => contributor.invitationId === personToRemind.id,
    );
    const targetId =
      !targetIsOwner && contributorTarget?.invitationId
        ? contributorTarget.invitationId
        : null;
    try {
      const reminderToSave = {
        targetId,
        frequency: frequency.id,
        targetIsOwner,
      };
      let response;
      if (reminder) {
        response = await VaultsController.editReminder(
          vault.id,
          reminder.id,
          reminderToSave,
        );
      } else {
        response = await VaultsController.createReminder(
          vault.id,
          reminderToSave,
        );
      }
      setEditReminders(false);
      setReminder(response);
    } catch (e) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
    }
  };

  useEffect(() => {
    if (reminder) {
      const user = vault.contributors.find(
        (contributor) => contributor.invitationId === reminder.targetId,
      );

      if (user) {
        setPersonToRemind({
          id: user.invitationId || 0,
          value: nameAndEmail(user.name, user.email),
        });
      } else {
        const isOwner = reminder.targetIsOwner;
        if (isOwner) {
          setPersonToRemind({
            // We do this to mark that there's no invitation ID that matches the owner.
            id: 0,
            value: nameAndEmail(vault.owner.name, vault.owner.email),
          });
        }
      }
      const frequencyOption = frequencyOptions.find(
        (opt: SelectType) => opt.id === reminder.frequency,
      );
      if (frequencyOption) {
        setFrequency(frequencyOption);
      }
    }
  }, [reminder]);

  const showInformation = () =>
    reminder && (
      <div className={styles.informationBox}>
        <div className={styles.information}>
          <LabeledText
            label="Remind"
            text={searchUserName(
              (personToRemind?.id as number) || vault.owner.id,
              vault,
            )}
          />
          <LabeledText
            label="to record"
            text={showFrequency(reminder.frequency)}
          />
        </div>
        <Button
          buttonSize={ButtonSize.Small}
          buttonStyle={ButtonStyle.PrimaryGhost}
          onClick={() => {
            setEditReminders(true);
          }}
          disabled={disabled}
        >
          <EditIcon className={styles.editIcon} />
        </Button>
      </div>
    );
  const showForm = () => (
    <div className={styles.form}>
      <div className={styles.formSelects}>
        <p className="text__body__semi__bold__medium__gray800">Remind</p>
        <Select
          options={createTheVaultOptions(vault)}
          optionSelected={{
            id: personToRemind ? personToRemind.id : 0,
            value: personToRemind ? personToRemind.value : '',
          }}
          onClickOption={(option) => {
            setPersonToRemind(option);
          }}
          value={personToRemind?.value}
          id="personToRemind"
          placeholder="Select User"
          title=""
          className={styles.selectPerson}
          smallOptions
        />
        <p
          className={classnames(
            styles.selectLabel,
            'text__body__semi__bold__medium__gray800',
          )}
        >
          to record
        </p>
        <Select
          options={frequencyOptions}
          optionSelected={{
            id: frequency.id,
            value: frequency.value,
          }}
          onClickOption={(option) => setFrequency(option)}
          value={frequency.value}
          id="frequency"
          title=""
          className={styles.selectFrequency}
          smallOptions
        />
      </div>
      <div className={styles.buttons}>
        <Button
          buttonSize={ButtonSize.Small}
          buttonStyle={ButtonStyle.PrimaryStroke}
          onClick={() => handleSaveReminder()}
          disabled={!personToRemind || disabled}
          className={styles.saveButton}
        >
          Save Changes
        </Button>
        <Button
          buttonSize={ButtonSize.Small}
          buttonStyle={ButtonStyle.PrimaryGhost}
          onClick={() => {
            setEditReminders(false);
            if (!reminder) {
              setRemindersEnabled(false);
            }
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
  const handleDeleteReminder = async () => {
    if (!reminder) {
      return;
    }
    try {
      await VaultsController.deleteReminder(vault.id, reminder.id);
      setReminder(null);
      setPersonToRemind(undefined);
      setFrequency(frequencyOptions[0]);
    } catch (e) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
    }
  };

  useEffect(() => {
    if (vault.reminder) {
      setReminder(vault.reminder);
    } else {
      setReminder(null);
      setRemindersEnabled(false);
      setPersonToRemind(undefined);
      setFrequency(frequencyOptions[0]);
    }
  }, [vault]);

  const toggleChange = () => {
    if (remindersEnabled && reminder) {
      handleDeleteReminder();
    }
    setRemindersEnabled(!remindersEnabled);
    setEditReminders(true);
  };

  const handleSendSampleEmail = async () => {
    try {
      await VaultsController.sendSampleEmail(vault.id);
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Email sent',
          message: '',
          type: NotificationType.Success,
        }),
      });
    } catch (e: any) {
      const notQuestionToSend = e?.errors?.find(
        (err: any) => err.errorCode === 40501,
      );
      if (notQuestionToSend) {
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'Your interview script is empty',
            message: 'Add prompts on the add prompts tab to set up reminders.',
            type: NotificationType.Error,
          }),
        });
        return;
      }
      const ToMuchRequest = e?.errors?.find(
        (err: any) => err.errorCode === 42900,
      );
      if (ToMuchRequest) {
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'Too much emails sent',
            message: 'Please try again later',
            type: NotificationType.Error,
          }),
        });
        return;
      }
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
    }
  };

  return (
    <div className={classnames(cardStyles.card, styles.reminderSection)}>
      <div className={styles.titleBox}>
        <p className="text__title2__gray800">Record reminders</p>
        <HelperText
          content={
            <span className="text__body__regular__small__gray700">
              You can set your vault to automatically send email reminders to
              record. We will send them the first unanswered prompt on your{' '}
              <Button
                buttonSize={ButtonSize.Small}
                buttonStyle={ButtonStyle.PrimaryGhostLink}
                onClick={() => {
                  goToPage(
                    RouteName.VaultDetail,
                    { id: vault.id },
                    { tab: VaultNavigationTabs.planner },
                  );
                }}
              >
                <span className="text__body__regular__small__brand500">
                  interview script
                </span>
              </Button>
              . Click{' '}
              <Button
                buttonSize={ButtonSize.Small}
                buttonStyle={ButtonStyle.PrimaryGhostLink}
                onClick={() => {
                  handleSendSampleEmail();
                }}
              >
                <span className="text__body__regular__small__brand500">
                  here
                </span>
              </Button>{' '}
              to send an sample email to yourself. Reminder emails go out on
              Monday mornings.
            </span>
          }
          icon={<InfoIcon className={styles.infoIcon} />}
        />
      </div>
      <div className={styles.toggleAndText}>
        <Toggle
          id="reminderToggle"
          checked={remindersEnabled}
          onChangeFn={() => toggleChange()}
          disabled={disabled}
          tooltipText="Upgrade the vault to enable this feature."
        />
        <span className="text__body__regular__medium__gray800">
          Email reminders
        </span>
      </div>
      {remindersEnabled && (editReminders ? showForm() : showInformation())}
    </div>
  );
};

export { ReminderSection };
